import { useEffect, useState, useRef } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Container from "@/components/Container";
import PropertyCard from "@/components/PropertyCard";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { LogoMark } from "@/components/Logo";
import HeroVideo, { HERO_SOURCES, INTERLUDE_SOURCES } from "@/components/HeroVideo";
import Parallax, { ParallaxImage } from "@/components/Parallax";
import { api } from "@/lib/api";
import { safeImg } from "@/lib/safeImg";
import { HOME } from "@/constants/testIds";
import { ArrowUpRight, ArrowRight } from "lucide-react";

// Dark, low-key cinematic luxury interior — feels like a Hermès / Aman / Aesop interior
const HERO_POSTER = "https://images.unsplash.com/photo-1618219740975-d40978bb7378?w=2400&q=90&auto=format&fit=crop";
// Period English property exterior at dusk
const INTERLUDE_POSTER = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=90";
// Marble / material detail
const MATERIAL_POSTER = "https://images.unsplash.com/photo-1604147706283-d7119b5b822c?w=2000&q=90";
// English country estate at dusk
const COUNTRY_POSTER = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=2400&q=90";
const FALLBACK_BEFORE = "https://images.pexels.com/photos/15535241/pexels-photo-15535241.jpeg";
const FALLBACK_AFTER  = "https://images.unsplash.com/photo-1748063578185-3d68121b11ff";

export default function Home() {
  const { cms } = useOutletContext() || {};
  const home = cms?.home || {};
  const [props, setProps] = useState([]);
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    api.get("/public/properties?limit=6").then(r => setProps(r.data || [])).catch(() => {});
    api.get("/public/case-studies?limit=3").then(r => setStudies(r.data || [])).catch(() => {});
  }, []);
  const featuredStudy = studies[0];

  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef, offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroProgress, [0, 1], [0, -90]);
  const heroOpacity = useTransform(heroProgress, [0, 0.7, 1], [1, 0.4, 0]);
  const heroScale = useTransform(heroProgress, [0, 1], [1, 1.06]);

  return (
    <div>
      {/* ────────────── HERO — Dark cinematic ────────────── */}
      <section ref={heroRef} className="relative h-screen min-h-[760px] overflow-hidden bg-ink-950">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <HeroVideo
            sources={HERO_SOURCES}
            poster={HERO_POSTER}
            overlayClassName="bg-gradient-to-r from-ink-950 via-ink-950/65 to-ink-950/40"
          />
          {/* extra bottom fade for legibility */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink-950 to-transparent pointer-events-none" />
        </motion.div>

        {/* corner brand mark */}
        <div className="absolute top-[110px] left-0 right-0 z-10">
          <Container className="flex items-center gap-4">
            <span className="h-px w-10 bg-ruby" />
            <span className="font-mono uppercase tracking-[0.3em] text-[0.65rem] text-cream-100/70">
              No. 01 — England — Est. Rubys
            </span>
          </Container>
        </div>

        <Container className="relative z-10 h-full flex flex-col justify-end pb-24 lg:pb-32">
          <motion.div style={{ y: heroY, opacity: heroOpacity }}>
            <h1 className="serif-display text-[3.25rem] sm:text-[4.5rem] lg:text-[6.5rem] xl:text-[7.5rem] text-cream-100 max-w-[18ch] leading-[0.95]">
              Signature property <span className="serif-italic text-ruby-light">transformations.</span>
            </h1>

            <div className="mt-12 max-w-md">
              <p className="text-cream-100/70 text-sm lg:text-base leading-[1.85]">
                We acquire homes of character and rare opportunity — then re-imagine them, room by room, into definitive Rubys.Group residences.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6">
              <Link to="/properties" className="btn-cream-on-dark" data-testid={HOME.ctaPrimary}>
                View the Portfolio <ArrowUpRight size={14} />
              </Link>
              <Link to="/sell" className="btn-link-dark" data-testid={HOME.ctaSecondary}>
                Sell to Rubys
              </Link>
              <Link to="/investors" className="btn-link-dark" data-testid={HOME.ctaTertiary}>
                Partner with the House
              </Link>
            </div>
          </motion.div>
        </Container>

        {/* bottom signature line */}
        <motion.div className="absolute bottom-8 left-0 right-0 z-10" style={{ opacity: heroOpacity }}>
          <Container className="flex items-center justify-between text-[0.6rem] font-mono uppercase tracking-[0.3em] text-cream-100/35">
            <span>A house, considered.</span>
            <span className="hidden md:inline">Scroll · Discover</span>
          </Container>
        </motion.div>
      </section>

      {/* ────────────── FOREWORD — quiet pause ────────────── */}
      <section className="py-32 lg:py-48 bg-cream-100">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            <div className="lg:col-span-3">
              <p className="label-mono">No. 02 — Foreword</p>
            </div>
            <Parallax speed={0.1} className="lg:col-span-9">
              <p className="serif-display text-2xl md:text-4xl lg:text-[3rem] xl:text-[3.5rem] text-ink-700 !leading-[1.28] max-w-[24ch] md:max-w-[28ch]">
                A property house — <span className="serif-italic text-ruby">not a property shop.</span> We see character where others see condition, and finish where others see flaw.
              </p>
              <div className="mt-14 flex items-center gap-4">
                <span className="h-px w-10 bg-ruby" />
                <Link to="/about" className="btn-link">Read the manifesto</Link>
              </div>
            </Parallax>
          </div>
        </Container>
      </section>

      {/* ────────────── MATERIAL DETAIL — quiet image ────────────── */}
      <section className="relative h-[80vh] overflow-hidden bg-ink-950">
        <ParallaxImage src={MATERIAL_POSTER} alt="" strength={0.25} />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-ink-950/10" />
        <Container className="relative h-full flex items-center z-10">
          <div className="max-w-xl">
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-ruby" />
              <p className="font-mono uppercase tracking-[0.3em] text-[0.65rem] text-ruby-light">Material confidence</p>
            </div>
            <h2 className="serif-display mt-8 text-4xl md:text-5xl lg:text-6xl text-cream-100">
              A calm, premium palette — <span className="serif-italic text-ruby-light">never overdesigned.</span>
            </h2>
          </div>
        </Container>
      </section>

      {/* ────────────── FEATURED TRANSFORMATION ────────────── */}
      <section className="py-32 lg:py-40 bg-cream-100">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
            <Parallax speed={0.08} className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              <p className="label-mono">No. 03 — Featured</p>
              <h2 className="serif-display text-4xl md:text-5xl lg:text-[3.75rem] text-ink-700 mt-8 leading-[1.05]">
                From overlooked <span className="serif-italic text-ruby">to outstanding.</span>
              </h2>
              <p className="mt-10 text-ink-100 leading-[1.85] text-base max-w-md">
                Each property is selected for character, opportunity and architectural potential. The Rubys finish reveals what was always there — and was never seen.
              </p>
              <Link to={featuredStudy ? `/before-after/${featuredStudy.id}` : "/before-after"} className="btn-link mt-12 inline-flex">
                {featuredStudy ? "View case study" : "Explore transformations"} <ArrowRight size={14} />
              </Link>
            </Parallax>
            <div className="lg:col-span-7">
              <BeforeAfterSlider
                before={safeImg(featuredStudy?.before_images?.[0], FALLBACK_BEFORE)}
                after={safeImg(featuredStudy?.after_images?.[0], FALLBACK_AFTER)}
                tone="light"
              />
              <p className="mt-5 font-mono text-[0.65rem] uppercase tracking-[0.3em] text-ink-100">
                Drag to reveal · A Rubys signature
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ────────────── DARK CINEMATIC INTERLUDE ────────────── */}
      <section className="relative h-[80vh] lg:h-[95vh] overflow-hidden bg-ink-950">
        <HeroVideo
          sources={INTERLUDE_SOURCES}
          poster={INTERLUDE_POSTER}
          overlayClassName="bg-gradient-to-r from-ink-950 via-ink-950/85 to-ink-950/30"
        />
        <Container className="relative h-full flex items-center z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-ruby" />
              <p className="font-mono uppercase tracking-[0.3em] text-[0.65rem] text-ruby-light">A house, considered.</p>
            </div>
            <h2 className="serif-display mt-10 text-4xl md:text-6xl lg:text-[5.5rem] text-cream-100 leading-[1.02]">
              Built quietly.
              <br /><span className="serif-italic text-ruby-light">Finished completely.</span>
            </h2>
            <p className="mt-10 text-cream-100/85 max-w-lg leading-[1.85] text-base lg:text-lg">
              Every Rubys home is led by design and executed with the patience of a craftsman — no shortcut, no compromise on the finish.
            </p>
          </div>
        </Container>
      </section>

      {/* ────────────── METHOD ────────────── */}
      <section className="py-32 lg:py-44 bg-cream-100">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
            <div className="lg:col-span-5">
              <p className="label-mono">No. 04 — Practice</p>
              <h2 className="serif-display text-4xl md:text-5xl lg:text-[4rem] text-ink-700 mt-8 leading-[1.05]">
                A practice, <span className="serif-italic text-ruby">quietly held.</span>
              </h2>
            </div>
            <Parallax speed={0.08} className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-ink-100 leading-[1.85] max-w-lg">
                Every house we touch passes through the same considered hands — patient sourcing, careful redesign and a finish that holds its line. There is no formula on display; the work simply asks to be lived in.
              </p>
              <Link to="/process" className="btn-link mt-10 inline-flex">A closer look <ArrowRight size={14} /></Link>
            </Parallax>
          </div>

          {/* Editorial three-mark statement — quiet, vague, luxury */}
          <div className="border-t border-ink-700/15 grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              ["Discretion", "We move privately. The right opportunity rarely announces itself."],
              ["Composition", "Plans before finishes. Light before luxury. Architecture before adornment."],
              ["Restraint",   "We leave only what belongs — and let the house speak for itself."],
            ].map(([title, sub], i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="md:border-r last:border-r-0 border-ink-700/10 py-12 lg:py-16 md:px-8 first:md:pl-0 last:md:pr-0"
              >
                <p className="editorial-numeral text-2xl lg:text-3xl leading-none">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="serif-light text-3xl lg:text-4xl text-ink-700 mt-6">{title}<span className="text-ruby">.</span></h3>
                <p className="mt-5 text-ink-100 text-base leading-[1.8] max-w-sm">{sub}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* ────────────── PORTFOLIO ────────────── */}
      <section className="py-32 lg:py-44 bg-cream-200">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <p className="label-mono">No. 05 — Portfolio</p>
              <h2 className="serif-display text-4xl md:text-5xl lg:text-[4rem] text-ink-700 mt-8 leading-[1.05]">
                Current & <span className="serif-italic text-ruby">recent</span> projects.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <Link to="/properties" className="btn-link inline-flex">View the full portfolio <ArrowRight size={14} /></Link>
            </div>
          </div>

          {props.length === 0 ? (
            <div className="surface-paper p-16 lg:p-24 text-center">
              <LogoMark size={48} className="text-ruby mx-auto" />
              <p className="label-mono mt-10">Curating the next chapter</p>
              <p className="serif-display text-3xl md:text-4xl text-ink-700 mt-6 max-w-2xl mx-auto !leading-[1.28]">
                Our next signature transformations are being assembled.
              </p>
              <Link to="/contact" className="btn-ruby-solid mt-12">Register interest <ArrowUpRight size={14} /></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-24">
              {props.slice(0, 6).map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ delay: (i % 3) * 0.1, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                >
                  <PropertyCard p={p} />
                </motion.div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* ────────────── ENGAGE ────────────── */}
      <section className="py-32 lg:py-44 bg-cream-100">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6">
              <p className="label-mono">No. 06 — Engage</p>
              <h2 className="serif-display text-4xl md:text-5xl lg:text-[4.5rem] text-ink-700 mt-8 leading-[1.05]">
                A property, a partnership, <span className="serif-italic text-ruby">a proposal.</span>
              </h2>
              <p className="mt-10 text-ink-100 max-w-md leading-[1.85] text-sm lg:text-base">
                Three ways to engage with Rubys.Group. Every enquiry is read by the team and answered within one working day.
              </p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex flex-col">
              {[
                { label: "Sell a property", to: "/sell", desc: "Probate · distressed · derelict · under-presented." },
                { label: "Become a partner", to: "/investors", desc: "Investors · agents · sourcers · finance." },
                { label: "Join the register", to: "/work-with-us", desc: "Trades · designers · architects · surveyors." },
              ].map((c) => (
                <Link key={c.label} to={c.to} className="group block border-t border-ink-700/15 last:border-b py-8 flex items-center justify-between gap-6 hover:pl-3 transition-all duration-700">
                  <div>
                    <p className="serif-display text-3xl lg:text-[2.25rem] text-ink-700 group-hover:text-ruby transition-colors">{c.label}</p>
                    <p className="text-[0.7rem] font-mono uppercase tracking-[0.22em] text-ink-100 mt-3">{c.desc}</p>
                  </div>
                  <ArrowUpRight className="text-ink-100 group-hover:text-ruby group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" size={22} strokeWidth={1.3} />
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
